import {
  apply, createMatch, redact, type Action, type Dictionary, type MatchConfig,
  type MatchState,
} from '@nt2/engine';
import type { Transport, TransportHandlers } from './transport.js';

/**
 * Solo and pass-and-play, with the authority running in this tab.
 *
 * It is the same engine, the same redaction and the same action names as the
 * networked room — only the wire is missing. That means solo play exercises
 * the real rules rather than a simplified copy, and a bug found offline is a
 * bug fixed online.
 */
export class LocalTransport implements Transport {
  readonly kind = 'local';
  #state: MatchState;
  #timer: number;
  /**
   * A closed transport is inert.
   *
   * Without this a stale reference could still be sent an action — the clock
   * tick from a screen that was being torn down was enough — and it would
   * answer with the state of the match that had just been left, dragging the
   * whole interface back into it.
   */
  #closed = false;

  constructor(
    private h: TransportHandlers,
    /**
     * The dictionary to judge by, asked for each time. It used to be handed
     * over once, at the start, which was whatever list had loaded by then —
     * so a solo match set to another list, or to British spelling, was judged
     * by the default one.
     */
    private dictNow: () => Dictionary,
    private playerId: string,
    playerName: string,
    config: Partial<MatchConfig>,
  ) {
    this.#state = createMatch('solo', config);
    apply(this.#state, { t: 'join', playerId, name: playerName }, this.dict, Date.now());
    this.h.onStatus('open');
    this.#push();
    // The engine has no clock of its own, so someone has to advance it for
    // timed matches and cooldown expiry.
    this.#timer = window.setInterval(() => {
      const before = this.#state.version;
      apply(this.#state, { t: 'tick' }, this.dict, Date.now());
      if (this.#state.version !== before) this.#push();
    }, 1000);
  }

  private get dict(): Dictionary { return this.dictNow(); }

  send(action: Action): void {
    if (this.#closed) return;
    const res = apply(this.#state, action, this.dict, Date.now());
      // When the outcome carries events, those are the player-facing account of
      // what happened and the error string is the same thing again — a refused
      // take-two would otherwise toast twice.
    if (!res.ok && res.error && res.events.length === 0) this.h.onError(res.error);
    if (res.events.length) this.h.onEvents(res.events);
    this.#push();
  }

  #push(): void {
    if (this.#closed) return;
    const seatId = this.#state.players.get(this.playerId)?.seatId ?? null;
    this.h.onState(redact(this.#state, { forSeatId: seatId || null }));
  }

  close(): void {
    if (this.#closed) return;
    this.#closed = true;
    clearInterval(this.#timer);
    this.h.onStatus('closed');
  }
}
